import { useState, useEffect, useMemo } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import {
  IndianRupee, TrendingUp, Building2, BarChart3, ArrowUpRight,
  ArrowDownRight, Minus, Sparkles, Target, AlertCircle, Users,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import DashboardKpiCard from '@/components/ux/DashboardKpiCard';
import { getOverallStats, getAllCompanies } from '@/services/apiClient';
import { cn } from '@/lib/utils';

/* ── colours ─────────────────────────────────────────────────────── */
const PLAN_PALETTE = {
  starter:      { fill: '#64748b', light: '#f1f5f9' },
  professional: { fill: '#3b82f6', light: '#eff6ff' },
  growth:       { fill: '#14b8a6', light: '#f0fdfa' },
  scale:        { fill: '#8b5cf6', light: '#f5f3ff' },
};
const STATUS_COLORS = {
  active:       '#10b981',
  trial:        '#3b82f6',
  grace_period: '#f59e0b',
  expired:      '#ef4444',
  cancelled:    '#94a3b8',
};
const STATUS_LABEL = {
  active: 'Active', trial: 'Trial', grace_period: 'Grace', expired: 'Expired', cancelled: 'Cancelled',
};

/* ── helpers ─────────────────────────────────────────────────────── */
function fmt(n) {
  if (n == null || !isFinite(n) || isNaN(n)) return '₹0';
  if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(1)}Cr`;
  if (n >= 100_000)    return `₹${(n / 100_000).toFixed(1)}L`;
  if (n >= 1_000)      return `₹${(n / 1_000).toFixed(1)}K`;
  return `₹${Math.round(n)}`;
}
function pct(a, b) { return b > 0 ? Math.round((a / b) * 100) : 0; }

/* ── custom tooltip ──────────────────────────────────────────────── */
function ChartTooltip({ active, payload, label, prefix = '₹' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-card px-3 py-2 shadow-lg text-xs">
      {label && <p className="mb-1 font-semibold text-muted-foreground">{label}</p>}
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-foreground">{p.name}:</span>
          <span className="font-bold text-foreground">{prefix}{(p.value || 0).toLocaleString('en-IN')}</span>
        </div>
      ))}
    </div>
  );
}

/* ── loading skeleton ────────────────────────────────────────────── */
function PageSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        <Skeleton className="lg:col-span-2 h-72 rounded-xl" />
        <Skeleton className="h-72 rounded-xl" />
      </div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );
}

/* ── growth badge ────────────────────────────────────────────────── */
function GrowthBadge({ value }) {
  if (value == null) return null;
  const pos = value > 0;
  const zero = value === 0;
  return (
    <span className={cn(
      'inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold',
      zero ? 'bg-muted text-muted-foreground'
      : pos ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
            : 'bg-red-500/10 text-red-700 dark:text-red-400'
    )}>
      {zero ? <Minus className="h-3 w-3" />
        : pos ? <ArrowUpRight className="h-3 w-3" />
              : <ArrowDownRight className="h-3 w-3" />}
      {Math.abs(value)}%
    </span>
  );
}

/* ── main ────────────────────────────────────────────────────────── */
export default function SuperAdminEarningsPage() {
  const [stats, setStats]       = useState(null);
  const [companies, setCompanies] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([getOverallStats(), getAllCompanies()])
      .then(([s, c]) => { setStats(s.data); setCompanies(c.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  /* derived ── */
  const mrr           = stats?.mrr || 0;
  const arr           = mrr * 12;
  const activeCount   = stats?.activeCompanies || 0;
  const trialCount    = stats?.trialCompanies || 0;
  const avgRevenue    = activeCount > 0 ? Math.round(mrr / activeCount) : 0;
  const revenueByPlan = useMemo(() => stats?.revenueByPlan || [], [stats]);
  const trend         = useMemo(() => stats?.monthlyRevenueTrend || [], [stats]);
  // Share is list value against the list-value total. Dividing by MRR mixed two
  // different measures — list price against what is actually charged — and
  // produced percentages that did not add to 100.
  const planListTotal = useMemo(
    () => revenueByPlan.reduce((sum, p) => sum + (p.listValue || 0), 0),
    [revenueByPlan],
  );

  /* MoM growth from trend */
  const momGrowth = useMemo(() => {
    if (trend.length < 2) return null;
    const cur  = trend[trend.length - 1]?.revenue || 0;
    const prev = trend[trend.length - 2]?.revenue || 0;
    if (prev === 0) return cur > 0 ? 100 : null;
    return Math.round(((cur - prev) / prev) * 100);
  }, [trend]);

  /* projected next month: simple linear extrapolation from last 3 months */
  const projected = useMemo(() => {
    if (trend.length < 2) return null;
    const last3 = trend.slice(-3).map(t => t.revenue || 0);
    const nonZero = last3.filter(v => v > 0);
    if (nonZero.length === 0) return null;
    const avg = last3.reduce((s, v) => s + v, 0) / last3.length;
    const first = last3[0];
    const last  = last3[last3.length - 1];
    const growth = (first > 0 && last3.length > 1)
      ? ((last - first) / first / (last3.length - 1)) * 100
      : 0;
    const result = Math.round(avg * (1 + Math.max(-50, Math.min(200, growth)) / 100));
    return isFinite(result) && result >= 0 ? result : null;
  }, [trend]);

  /* pie data: revenue by plan */
  const pieData = useMemo(
    () => revenueByPlan
      .filter(p => p.listValue > 0)
      .map(p => ({
        name:  p.name,
        value: p.listValue,
        slug:  p.slug,
        fill:  PLAN_PALETTE[p.slug]?.fill || '#6b7280',
      })),
    [revenueByPlan]
  );

  /* status distribution of companies */
  const statusDist = useMemo(() => {
    const counts = {};
    companies.forEach(c => {
      const s = c.subscription?.status || 'cancelled';
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).map(([status, count]) => ({
      name: STATUS_LABEL[status] || status,
      value: count,
      fill: STATUS_COLORS[status] || '#94a3b8',
    }));
  }, [companies]);

  /* per-company filtered table */
  const filteredCompanies = useMemo(
    () => companies
      .filter(c => statusFilter === 'all' || c.subscription?.status === statusFilter)
      .sort((a, b) => (b.subscription?.plan?.price || 0) - (a.subscription?.plan?.price || 0)),
    [companies, statusFilter]
  );

  if (loading) return <PageSkeleton />;

  return (
    <div className="space-y-5 pb-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground/60">Revenue Intelligence</p>
          <h1 className="text-xl font-bold tracking-tight text-foreground">Earnings</h1>
        </div>
        {projected != null && (
          <div className="flex items-center gap-2 rounded-xl border border-primary/20 bg-primary/5 px-3 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
            <span className="text-xs text-foreground/65">Projected next month:</span>
            <span className="text-sm font-black text-primary">{fmt(projected)}</span>
          </div>
        )}
      </div>

      {/* ── KPI strip ── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-4 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground/65">MRR</p>
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/10">
              <IndianRupee className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
          <p className="text-2xl font-black tabular-nums text-foreground">{fmt(mrr)}</p>
          <div className="flex items-center gap-1.5">
            <GrowthBadge value={momGrowth} />
            <span className="text-[10px] text-muted-foreground">vs last month</span>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground/65">ARR</p>
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
              <TrendingUp className="h-3.5 w-3.5 text-primary" />
            </div>
          </div>
          <p className="text-2xl font-black tabular-nums text-foreground">{fmt(arr)}</p>
          <p className="text-[10px] text-muted-foreground">Annual run rate (MRR × 12)</p>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground/65">Paying Companies</p>
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-500/10">
              <Building2 className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <p className="text-2xl font-black tabular-nums text-foreground">{activeCount}</p>
          <p className="text-[10px] text-muted-foreground">{trialCount} on free trial</p>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground/65">Avg Revenue</p>
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-500/10">
              <Target className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" />
            </div>
          </div>
          <p className="text-2xl font-black tabular-nums text-foreground">{fmt(avgRevenue)}</p>
          <p className="text-[10px] text-muted-foreground">Per paying company / month</p>
        </div>
      </div>

      {/* ── Revenue Trend + Plan Pie ── */}
      <div className="grid gap-3 lg:grid-cols-3">
        {/* Area chart */}
        <Card className="lg:col-span-2 rounded-xl border-border">
          <CardHeader className="pb-2 pt-4 px-5">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-semibold">Revenue Trend</CardTitle>
                <CardDescription className="text-xs">Monthly recurring revenue — last 6 months</CardDescription>
              </div>
              {momGrowth != null && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <GrowthBadge value={momGrowth} />
                  <span>MoM</span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="px-2 pb-4">
            {trend.length === 0 ? (
              <div className="flex h-52 items-center justify-center text-sm text-muted-foreground">No trend data yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#8b5cf6" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: 'hsl(var(--foreground) / 0.6)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'hsl(var(--foreground) / 0.6)', fontSize: 11 }} axisLine={false} tickLine={false}
                    tickFormatter={v => fmt(v)} width={60} />
                  <ReTooltip content={<ChartTooltip />} wrapperStyle={{ outline: 'none' }} />
                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#8b5cf6" strokeWidth={2.5}
                    fill="url(#revGrad)" dot={{ fill: '#8b5cf6', r: 4, strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 0 }} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Plan revenue pie */}
        <Card className="rounded-xl border-border">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm font-semibold">Revenue by Plan</CardTitle>
            <CardDescription className="text-xs">MRR contribution per plan tier</CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {pieData.length === 0 ? (
              <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">No plan data.</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={170}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={44} outerRadius={72}
                      paddingAngle={3} dataKey="value" isAnimationActive>
                      {pieData.map((entry) => (
                        <Cell key={entry.slug} fill={entry.fill} stroke="hsl(var(--background))" strokeWidth={2} />
                      ))}
                    </Pie>
                    <ReTooltip wrapperStyle={{ outline: 'none' }} content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0];
                      return (
                        <div className="rounded-xl border border-border bg-card px-3 py-2 shadow-lg text-xs">
                          <p className="font-semibold text-foreground">{d.name}</p>
                          <p className="text-foreground/70">{fmt(d.value)} <span className="text-foreground font-medium">({pct(d.value, mrr)}% of MRR)</span></p>
                        </div>
                      );
                    }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-1 space-y-1.5">
                  {pieData.map(p => (
                    <div key={p.slug} className="flex items-center justify-between gap-2 text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: p.fill }} />
                        <span className="capitalize text-foreground/70">{p.name}</span>
                      </div>
                      <span className="font-semibold text-foreground tabular-nums">{fmt(p.value)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Plan breakdown table + Status donut ── */}
      <div className="grid gap-3 lg:grid-cols-3">
        {/* Revenue by plan table */}
        <Card className="lg:col-span-2 rounded-xl border-border overflow-hidden">
          <CardHeader className="border-b border-border/60 bg-muted/20 px-5 py-3">
            <CardTitle className="text-sm font-semibold">Plan Breakdown</CardTitle>
            <CardDescription className="text-xs">Revenue, companies and contribution per tier</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/40">
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-foreground/65">Plan</th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider text-foreground/65">Price / mo</th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider text-foreground/65">Companies</th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider text-foreground/65">List value</th>
                  <th className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-foreground/65">Share</th>
                </tr>
              </thead>
              <tbody>
                {revenueByPlan.length === 0 ? (
                  <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">No plan data yet.</td></tr>
                ) : revenueByPlan.map(plan => {
                  const share = pct(plan.listValue, planListTotal);
                  const palette = PLAN_PALETTE[plan.slug] || { fill: '#6b7280', light: '#f9fafb' };
                  return (
                    <tr key={plan.slug} className="border-b border-border/30 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: palette.fill }} />
                          <span className="font-semibold text-foreground capitalize">{plan.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-foreground/70 tabular-nums">₹{(plan.price || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground tabular-nums">{plan.companyCount}</td>
                      <td className="px-4 py-3 text-right font-bold text-foreground tabular-nums">{fmt(plan.listValue)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${share}%`, background: palette.fill }} />
                          </div>
                          <span className="w-8 text-right text-[11px] text-foreground/65 tabular-nums">{share}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Company status donut */}
        <Card className="rounded-xl border-border">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm font-semibold">Account Status</CardTitle>
            <CardDescription className="text-xs">{companies.length} total companies</CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {statusDist.length === 0 ? (
              <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">No data.</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={170}>
                  <PieChart>
                    <Pie data={statusDist} cx="50%" cy="50%" innerRadius={44} outerRadius={72}
                      paddingAngle={3} dataKey="value" isAnimationActive>
                      {statusDist.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} stroke="hsl(var(--background))" strokeWidth={2} />
                      ))}
                    </Pie>
                    <ReTooltip wrapperStyle={{ outline: 'none' }} content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0];
                      return (
                        <div className="rounded-xl border border-border bg-card px-3 py-2 shadow-lg text-xs">
                          <p className="font-semibold text-foreground">{d.name}</p>
                          <p className="text-foreground/70">{d.value} companies ({pct(d.value, companies.length)}%)</p>
                        </div>
                      );
                    }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-1 space-y-1.5">
                  {statusDist.map(s => (
                    <div key={s.name} className="flex items-center justify-between gap-2 text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: s.fill }} />
                        <span className="text-foreground/70">{s.name}</span>
                      </div>
                      <span className="font-semibold text-foreground tabular-nums">{s.value} <span className="text-foreground/55 font-normal">({pct(s.value, companies.length)}%)</span></span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Forecast card ── */}
      {projected != null && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            {
              label: 'Projected MRR (next month)',
              value: fmt(projected),
              note: momGrowth != null ? `Based on ${momGrowth > 0 ? '+' : ''}${momGrowth}% MoM trend` : 'Linear extrapolation',
              icon: Sparkles,
              color: 'text-primary',
              bg: 'bg-primary/8',
            },
            {
              label: 'Projected ARR (next month × 12)',
              value: fmt(projected * 12),
              note: 'If current growth rate holds',
              icon: TrendingUp,
              color: 'text-emerald-600 dark:text-emerald-400',
              bg: 'bg-emerald-500/8',
            },
            {
              label: 'Revenue at risk (grace + expired)',
              value: fmt(
                companies
                  .filter(c => ['grace_period', 'expired'].includes(c.subscription?.status))
                  .reduce((s, c) => s + (c.subscription?.plan?.price || 0), 0)
              ),
              note: 'Monthly revenue from churned/churning accounts',
              icon: AlertCircle,
              color: 'text-destructive',
              bg: 'bg-destructive/8',
            },
          ].map(({ label, value, note, icon: Icon, color, bg }) => (
            <div key={label} className="rounded-xl border border-border bg-card p-4 flex gap-3 items-start">
              <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', bg)}>
                <Icon className={cn('h-4 w-4', color)} />
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground/65">{label}</p>
                <p className={cn('text-xl font-black tabular-nums', color)}>{value}</p>
                <p className="text-[11px] text-foreground/60 mt-0.5">{note}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Per-company revenue table ── */}
      <Card className="rounded-xl border-border overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 bg-muted/20 px-5 py-3">
          <div>
            <CardTitle className="text-sm font-semibold">Per-Company Revenue</CardTitle>
            <CardDescription className="text-xs">{filteredCompanies.length} companies</CardDescription>
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="h-8 rounded-lg border border-border bg-background px-2 text-xs text-foreground"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="grace_period">Grace Period</option>
            <option value="expired">Expired</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/40 bg-muted/10">
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-foreground/65">Company</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-foreground/65">Plan</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider text-foreground/65">Price / mo</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-foreground/65">Status</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider text-foreground/65">Interviews used</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.length === 0 ? (
                <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">No companies match filter.</td></tr>
              ) : filteredCompanies.map(c => {
                const status = c.subscription?.status || 'cancelled';
                const color  = STATUS_COLORS[status] || '#94a3b8';
                const price  = c.subscription?.plan?.price;
                return (
                  <tr key={c.companyId} className="border-b border-border/30 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 font-semibold text-foreground">{c.companyName}</td>
                    <td className="px-4 py-3 capitalize text-foreground/70">{c.subscription?.plan?.name || c.subscription?.planSlug || '—'}</td>
                    <td className="px-4 py-3 text-right font-medium text-foreground tabular-nums">
                      {price ? `₹${price.toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
                        style={{ background: `${color}18`, color }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
                        {STATUS_LABEL[status] || status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-foreground/70">
                      {c.subscription?.interviewsUsedThisMonth ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
